import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Banknote, PiggyBank, TrendingDown, Wallet, CalendarClock,
  MessageCircle, ShieldCheck, Target, Landmark, Receipt, Upload, Loader2,
  Activity,
} from 'lucide-react';
import { T, springs } from '../lib/tokens';
import { apiFetch } from '../lib/api';
import { Glass, GlassCard } from '../components/glass/Glass';

// Money dashboard — fed entirely by GET /finance/overview (one payload,
// all maths computed server-side by finance-engine). Polls while mounted so
// the view fills in live as Mike talks Penny through the intake interview.

const POLL_MS = 15000;
const PENNY_GREEN = '#2F8F5F';

const gbp0 = (n) => '£' + Math.round(Number(n) || 0).toLocaleString('en-GB');
const gbp2 = (n) => '£' + (Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const daysUntil = (dateStr) => {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - Date.now()) / 86400000);
};

const fmtMonth = (dateStr) => new Date(dateStr + 'T12:00:00Z')
  .toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });

const fmtLong = (dateStr) => new Date(dateStr + 'T12:00:00Z')
  .toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

const Chip = ({ children, color, bg }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', padding: '2px 8px',
    borderRadius: 10, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.02em',
    color, background: bg, whiteSpace: 'nowrap',
  }}>{children}</span>
);

const StatTile = ({ label, value, sub, color }) => (
  <div style={{ flex: 1, minWidth: 150, padding: '16px 20px' }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
      {label}
    </div>
    <div style={{ fontSize: 26, fontWeight: 800, color: color || T.text, letterSpacing: '-0.02em', marginTop: 4 }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 3 }}>{sub}</div>}
  </div>
);

const PromoChip = ({ account }) => {
  if (!account.promo_ends_on) return null;
  const days = daysUntil(account.promo_ends_on);
  if (days < 0) return <Chip color={T.red} bg={T.redLight}>promo expired</Chip>;
  const urgent = days <= 30;
  const warm = days <= 60;
  return (
    <Chip
      color={urgent ? T.red : warm ? T.amber : PENNY_GREEN}
      bg={urgent ? T.redLight : warm ? T.amberLight : 'rgba(47,143,95,0.10)'}
    >
      {account.promo_apr ?? 0}% ends in {days}d
    </Chip>
  );
};

const DebtRow = ({ account, maxBalance, payoffDate }) => {
  const frac = maxBalance > 0 ? Math.max(0.02, Number(account.balance) / maxBalance) : 0;
  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13.5, fontWeight: 650, color: T.text }}>{account.name}</span>
        {account.provider && <span style={{ fontSize: 11, color: T.textMuted }}>{account.provider}</span>}
        <span style={{ marginLeft: 'auto', fontSize: 14, fontWeight: 750, color: T.text }}>{gbp2(account.balance)}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 120, height: 7, borderRadius: 4, background: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: frac }}
            transition={springs.gentle}
            style={{ height: '100%', width: '100%', transformOrigin: 'left', borderRadius: 4,
              background: `linear-gradient(90deg, ${T.red}CC, ${T.red})` }}
          />
        </div>
        {account.apr != null && <Chip color={T.textMid} bg="rgba(0,0,0,0.05)">{account.apr}% APR</Chip>}
        <PromoChip account={account} />
        {account.min_payment != null && (
          <span style={{ fontSize: 11, color: T.textMuted }}>min {gbp0(account.min_payment)}/mo</span>
        )}
        {payoffDate && (
          <Chip color={PENNY_GREEN} bg="rgba(47,143,95,0.10)">paid off {fmtMonth(payoffDate)}</Chip>
        )}
      </div>
    </div>
  );
};

// Total-debt-by-month curve with the minimums-only baseline behind it; the gap
// between the two is the interest the plan saves. Bespoke SVG (no chart lib).
const PayoffTimeline = ({ payoff, baseline, accounts }) => {
  const plan = payoff.months.map(m => ({ date: m.date, total: m.totalClosing }));
  const start = { date: payoff.startDate, total: payoff.months[0]?.totalOpening ?? 0 };
  const planPts = [start, ...plan];
  const basePts = baseline && baseline.length ? [start, ...baseline] : null;

  const W = 820, H = 200, PAD_L = 52, PAD_R = 16, PAD_T = 14, PAD_B = 30;
  const allPts = basePts ? [...planPts, ...basePts] : planPts;
  const maxY = Math.max(...allPts.map(p => p.total), 1);
  const maxN = Math.max(planPts.length, basePts ? basePts.length : 0) - 1;

  const x = (i) => PAD_L + (i / Math.max(maxN, 1)) * (W - PAD_L - PAD_R);
  const y = (v) => PAD_T + (1 - v / maxY) * (H - PAD_T - PAD_B);
  const path = (pts) => pts.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.total).toFixed(1)}`).join(' ');

  // Promo expiries that land inside the plan horizon.
  const promoMarks = (accounts || [])
    .filter(a => a.kind === 'debt' && a.promo_ends_on)
    .map(a => {
      const idx = planPts.findIndex(p => p.date >= a.promo_ends_on);
      return idx > 0 ? { name: a.name, idx } : null;
    })
    .filter(Boolean);

  const tickEvery = Math.max(1, Math.ceil(planPts.length / 8));
  const yTicks = [0, 0.5, 1].map(f => Math.round(maxY * f));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {yTicks.map(v => (
        <g key={v}>
          <line x1={PAD_L} x2={W - PAD_R} y1={y(v)} y2={y(v)} stroke="rgba(0,0,0,0.06)" strokeWidth="1" />
          <text x={PAD_L - 8} y={y(v) + 3.5} textAnchor="end" fontSize="10" fill={T.textMuted}>{gbp0(v)}</text>
        </g>
      ))}
      {planPts.map((p, i) => (i % tickEvery === 0 && i > 0) ? (
        <text key={p.date} x={x(i)} y={H - 10} textAnchor="middle" fontSize="9.5" fill={T.textMuted}>
          {fmtMonth(p.date)}
        </text>
      ) : null)}

      {basePts && (
        <path d={path(basePts)} fill="none" stroke={T.textLight} strokeWidth="1.5" strokeDasharray="5 4" />
      )}

      <path
        d={`${path(planPts)} L${x(planPts.length - 1).toFixed(1)},${y(0)} L${x(0).toFixed(1)},${y(0)} Z`}
        fill="rgba(47,143,95,0.10)" stroke="none"
      />
      <path d={path(planPts)} fill="none" stroke={PENNY_GREEN} strokeWidth="2.5" strokeLinejoin="round" />

      {promoMarks.map(m => (
        <g key={m.name + m.idx}>
          <line x1={x(m.idx)} x2={x(m.idx)} y1={PAD_T} y2={H - PAD_B} stroke={T.amber} strokeWidth="1" strokeDasharray="3 3" />
          <text x={x(m.idx) + 3} y={PAD_T + 9} fontSize="9" fill={T.amber}>0% ends</text>
        </g>
      ))}

      {payoff.debtFreeDate && (
        <g>
          <circle cx={x(planPts.length - 1)} cy={y(0)} r="4.5" fill={PENNY_GREEN} />
          <text x={x(planPts.length - 1) - 4} y={y(0) - 9} textAnchor="end" fontSize="10.5" fontWeight="700" fill={PENNY_GREEN}>
            debt-free {fmtMonth(payoff.debtFreeDate)}
          </text>
        </g>
      )}
    </svg>
  );
};

// Day-by-day cash line with the zero axis, inflow markers, and the low point
// flagged — answers "when might I run out of cash" at a glance.
const CashflowChart = ({ cashflow }) => {
  const pts = cashflow.series;
  if (!pts || pts.length < 2) return null;
  const W = 820, H = 180, PL = 56, PR = 16, PT = 14, PB = 26;
  const maxY = Math.max(...pts.map(p => p.balance), 0);
  const minY = Math.min(...pts.map(p => p.balance), 0);
  const span = Math.max(maxY - minY, 1);
  const x = (i) => PL + (i / (pts.length - 1)) * (W - PL - PR);
  const y = (v) => PT + (1 - (v - minY) / span) * (H - PT - PB);
  const path = pts.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.balance).toFixed(1)}`).join(' ');

  const inflowDays = pts.map((p, i) => ({ p, i }))
    .filter(({ p }) => p.events.some(e => e.amount > 0));
  const minIdx = pts.findIndex(p => p.balance === cashflow.minBalance);
  const negative = cashflow.goesNegativeOn != null;
  const tickEvery = Math.max(1, Math.ceil(pts.length / 7));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {minY < 0 && (
        <rect x={PL} y={y(0)} width={W - PL - PR} height={Math.max(0, y(minY) - y(0))}
          fill="rgba(224,88,88,0.07)" />
      )}
      <line x1={PL} x2={W - PR} y1={y(0)} y2={y(0)} stroke={T.textLight} strokeWidth="1" strokeDasharray="4 4" />
      <text x={PL - 8} y={y(0) + 3.5} textAnchor="end" fontSize="10" fill={T.textMuted}>£0</text>

      {pts.map((p, i) => (i % tickEvery === 0 && i > 0) ? (
        <text key={p.date} x={x(i)} y={H - 8} textAnchor="middle" fontSize="9.5" fill={T.textMuted}>
          {new Date(p.date + 'T12:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
        </text>
      ) : null)}

      <path d={path} fill="none" stroke={negative ? T.red : PENNY_GREEN} strokeWidth="2.5" strokeLinejoin="round" />

      {inflowDays.map(({ p, i }) => (
        <g key={p.date}>
          <circle cx={x(i)} cy={y(p.balance)} r="3.5" fill={PENNY_GREEN} />
          <text x={x(i)} y={y(p.balance) - 7} textAnchor="middle" fontSize="9" fontWeight="700" fill={PENNY_GREEN}>
            +{gbp0(p.events.filter(e => e.amount > 0).reduce((s, e) => s + e.amount, 0))}
          </text>
        </g>
      ))}

      {minIdx >= 0 && (
        <g>
          <circle cx={x(minIdx)} cy={y(cashflow.minBalance)} r="4.5"
            fill={cashflow.minBalance < 0 ? T.red : T.amber} />
          <text x={x(minIdx)} y={y(cashflow.minBalance) + 16} textAnchor="middle" fontSize="10" fontWeight="700"
            fill={cashflow.minBalance < 0 ? T.red : T.amber}>
            low {gbp0(cashflow.minBalance)}
          </text>
        </g>
      )}
    </svg>
  );
};

const Sparkline = ({ points, color, width = 220, height = 44 }) => {
  if (!points || points.length < 2) return null;
  const max = Math.max(...points, 1), min = Math.min(...points, 0);
  const span = Math.max(max - min, 1);
  const x = (i) => (i / (points.length - 1)) * (width - 4) + 2;
  const y = (v) => 4 + (1 - (v - min) / span) * (height - 8);
  const d = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width, height, display: 'block' }}>
      <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
};

const BudgetBar = ({ budget }) => {
  const income = Math.max(Number(budget.income) || 0, 0);
  if (!income) return null;
  const seg = (v, color, label) => {
    const w = Math.max((v / income) * 100, 0);
    return w > 0 ? (
      <div title={`${label}: ${gbp0(v)}`} style={{ width: `${w}%`, background: color, height: '100%' }} />
    ) : null;
  };
  const surplus = Math.max(Number(budget.surplus) || 0, 0);
  return (
    <div style={{ height: 14, borderRadius: 7, overflow: 'hidden', display: 'flex', background: 'rgba(0,0,0,0.05)' }}>
      {seg(budget.fixed, T.violet, 'Fixed')}
      {seg(budget.variable, T.blue, 'Variable')}
      {seg(surplus, PENNY_GREEN, 'Surplus')}
    </div>
  );
};

export const FinanceView = ({ onChatWithPenny }) => {
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');

  const importText = async (text, label) => {
    setImporting(true);
    setImportMsg(null);
    try {
      const res = await apiFetch(`/finance/statements?label=${encodeURIComponent(label)}`, {
        method: 'POST', headers: { 'Content-Type': 'text/csv' }, body: text,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setImportMsg(`Imported ${data.imported} transactions (${data.duplicates} duplicates skipped), ${data.date_range.from} → ${data.date_range.to}. Ask Penny where to cut.`);
      setPasteOpen(false);
      setPasteText('');
    } catch (err) {
      setImportMsg(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const text = await file.text();
    await importText(text, file.name.replace(/\.csv$/i, '').slice(0, 60));
  };

  useEffect(() => {
    let alive = true;
    let loadedOnce = false; // only surface errors before the first successful load
    const load = async () => {
      try {
        const res = await apiFetch('/finance/overview');
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
        const data = await res.json();
        if (alive) { loadedOnce = true; setOverview(data); setError(null); }
      } catch (err) {
        if (alive && !loadedOnce) setError(err.message);
      }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const debts = useMemo(() => {
    if (!overview) return [];
    const order = new Map((overview.payoff?.debts || []).map(d => [d.id, d]));
    return overview.accounts
      .filter(a => a.kind === 'debt' && Number(a.balance) > 0)
      .sort((a, b) => (order.get(a.id)?.monthsToPayoff ?? 9e9) - (order.get(b.id)?.monthsToPayoff ?? 9e9));
  }, [overview]);

  if (error && !overview) {
    return <Glass style={{ padding: 28 }} hover={false}>
      <div style={{ fontSize: 13, color: T.textMid }}>Couldn't load the money view: {error}</div>
    </Glass>;
  }
  if (!overview) {
    return <div style={{ padding: 40, textAlign: 'center', color: T.textMuted, fontSize: 13 }}>Loading…</div>;
  }

  // ── Empty state: point Mike at Penny ──
  if (overview.empty) {
    return (
      <Glass style={{ padding: 48, maxWidth: 560, margin: '60px auto', textAlign: 'center' }} hover={false}>
        <div style={{
          width: 56, height: 56, borderRadius: 18, margin: '0 auto 18px',
          background: `linear-gradient(135deg, ${PENNY_GREEN}E6, ${PENNY_GREEN})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 6px 18px ${PENNY_GREEN}50`,
        }}>
          <PiggyBank size={26} color="#fff" />
        </div>
        <div style={{ fontSize: 19, fontWeight: 750, color: T.text, marginBottom: 8 }}>
          Penny doesn't have your numbers yet
        </div>
        <div style={{ fontSize: 13.5, color: T.textMid, lineHeight: 1.65, marginBottom: 22 }}>
          Tell her your debts, income and outgoings in chat — she records them as you talk,
          and this dashboard fills in live. Have your statements or banking app handy for the
          balances, rates and any 0% end dates.
        </div>
        <motion.button
          onClick={onChatWithPenny}
          whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} transition={springs.snappy}
          style={{
            padding: '11px 22px', borderRadius: 12, border: 'none', cursor: 'pointer',
            background: `linear-gradient(135deg, ${PENNY_GREEN}E6, ${PENNY_GREEN})`,
            color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
            display: 'inline-flex', alignItems: 'center', gap: 8,
            boxShadow: `0 4px 14px ${PENNY_GREEN}50`,
          }}
        >
          <MessageCircle size={16} /> Chat with Penny
        </motion.button>
      </Glass>
    );
  }

  const { totals, budget, plan, payoff, comparison, baseline, pension_projection, snapshots,
    month_budget: mb, set_asides: setAsides, spending, cashflow } = overview;
  const first = snapshots[0], last = snapshots[snapshots.length - 1];
  const debtDelta = first && last ? last.total_debt - first.total_debt : 0;
  const interestSaved = comparison?.deltas?.avalancheVsMinimums?.interestSaved;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 1060, margin: '0 auto' }}>

      {/* Net position strip */}
      <Glass style={{ display: 'flex', flexWrap: 'wrap', padding: '6px 8px' }} hover={false}>
        <StatTile label="Total debt" value={gbp0(totals.debt)} color={totals.debt > 0 ? T.red : PENNY_GREEN}
          sub={first && last && debtDelta !== 0 ? `${debtDelta < 0 ? '−' : '+'}${gbp0(Math.abs(debtDelta))} since ${fmtMonth(first.recorded_on)}` : 'progress shows after balance updates'} />
        <StatTile label="Savings" value={gbp0(totals.savings)}
          sub={plan?.emergency_fund_target ? `target ${gbp0(plan.emergency_fund_target)}` : null} />
        <StatTile label="Pension" value={gbp0(totals.pension)}
          sub={plan?.pension_monthly_target ? `restart at ${gbp0(plan.pension_monthly_target)}/mo` : 'contributions paused'} />
        {mb ? (
          <StatTile label="Left this month" value={gbp0(mb.left)} color={mb.left >= 0 ? PENNY_GREEN : T.red}
            sub={`after set-asides & debt budget${mb.incomeEstimated ? ' · income est.' : ''}`} />
        ) : (
          <StatTile label="Monthly surplus" value={gbp0(budget.surplus)} color={budget.surplus >= 0 ? PENNY_GREEN : T.red}
            sub={`${gbp0(budget.income)} in − ${gbp0(budget.outgoings)} out`} />
        )}
      </Glass>

      {/* This month + set-asides */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)', gap: 18, alignItems: 'start' }}>
        {mb && (
          <GlassCard title={`This month — ${fmtMonth(mb.month + '-15')}`} icon={CalendarClock}
            subtitle={mb.incomeEstimated ? 'income is the baseline estimate — tell Penny what’s actually coming in' : `${gbp0(mb.receivedIncome)} of ${gbp0(mb.expectedIncome)} received`}
            iconBg="rgba(47,143,95,0.12)" iconColor={PENNY_GREEN}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {mb.inflows.length > 0 && (
                <div style={{ borderBottom: '1px solid rgba(0,0,0,0.05)', paddingBottom: 8 }}>
                  {mb.inflows.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '3px 0' }}>
                      <span style={{ color: T.textMid, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.label}{f.source ? <span style={{ color: T.textLight }}> · {f.source}</span> : null}
                      </span>
                      <span style={{ fontSize: 10.5, color: T.textMuted }}>{f.expected_on?.slice(8)}th</span>
                      <Chip color={f.status === 'received' ? PENNY_GREEN : T.amber}
                        bg={f.status === 'received' ? 'rgba(47,143,95,0.10)' : T.amberLight}>
                        {f.status}
                      </Chip>
                      <span style={{ fontWeight: 650, color: PENNY_GREEN }}>+{gbp0(f.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
              {[
                ['Expected in', mb.expectedIncome, PENNY_GREEN],
                ['Fixed outgoings', -mb.fixed, T.textMid],
                ['Variable living', -mb.variable, T.textMid],
                ['Set-asides (tax & pots)', -mb.setAsidesTotal, T.violet],
                ['Debt budget', -mb.debtBudget, T.red],
              ].map(([label, v, color]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                  <span style={{ color: T.textMid }}>{label}</span>
                  <span style={{ fontWeight: 600, color }}>{v < 0 ? `−${gbp0(-v)}` : gbp0(v)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: 8, fontSize: 14 }}>
                <span style={{ fontWeight: 700, color: T.text }}>Left</span>
                <span style={{ fontWeight: 800, color: mb.left >= 0 ? PENNY_GREEN : T.red }}>{gbp0(mb.left)}</span>
              </div>
            </div>
          </GlassCard>
        )}

        <GlassCard title="Set-asides" subtitle="earmarked for VAT, taxes & pots" icon={Landmark}
          iconBg={T.violetLight} iconColor={T.violet}>
          {setAsides && setAsides.length ? setAsides.map(s => {
            const frac = s.target_amount ? Math.min(1, s.provisioned / s.target_amount) : null;
            const dueSoon = s.due_days != null && s.due_days <= 60;
            const dueUrgent = s.due_days != null && s.due_days <= 30;
            return (
              <div key={s.id} style={{ padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 650, color: T.text }}>{s.label}</span>
                  <span style={{ fontSize: 11, color: T.textMuted }}>
                    {s.rule_type === 'pct_of_income' ? `${s.pct}% of ${s.apply_to || 'income'}` : `${gbp0(s.amount_this_month)}/mo`}
                  </span>
                  {s.due_on && (
                    <Chip color={dueUrgent ? T.red : dueSoon ? T.amber : T.textMid}
                      bg={dueUrgent ? T.redLight : dueSoon ? T.amberLight : 'rgba(0,0,0,0.05)'}>
                      due {s.due_on} ({s.due_days}d)
                    </Chip>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 700, color: T.violet }}>
                    {gbp0(s.provisioned)}{s.target_amount ? ` / ${gbp0(s.target_amount)}` : ''}
                  </span>
                </div>
                {frac != null && (
                  <div style={{ marginTop: 6, height: 5, borderRadius: 3, background: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                    <div style={{ width: `${frac * 100}%`, height: '100%', borderRadius: 3,
                      background: frac >= 1 ? PENNY_GREEN : dueUrgent && frac < 1 ? T.red : T.violet }} />
                  </div>
                )}
              </div>
            );
          }) : (
            <div style={{ fontSize: 12.5, color: T.textMuted, lineHeight: 1.6 }}>
              Nothing earmarked yet. Tell Penny — "put aside £600 a month for VAT, due 7 August"
              or "set aside 20% of Pryke income for tax" — and it shows up here.
            </div>
          )}
        </GlassCard>
      </div>

      {/* Infeasible-plan warning takes precedence over everything */}
      {payoff && !payoff.feasible && (
        <Glass style={{ padding: '14px 20px', border: `1px solid ${T.red}40` }} hover={false} tint="red">
          <div style={{ fontSize: 13, fontWeight: 650, color: T.red }}>
            The plan budget doesn't cover minimum payments — short by {gbp2(payoff.shortfall)}/mo. Talk it through with Penny.
          </div>
        </Glass>
      )}

      {/* Cash flow — when might the cash run out */}
      {cashflow && cashflow.series && cashflow.series.length > 1 && (
        <GlassCard
          title="Cash flow — next 45 days"
          icon={Activity}
          iconBg={cashflow.goesNegativeOn ? T.redLight : 'rgba(47,143,95,0.12)'}
          iconColor={cashflow.goesNegativeOn ? T.red : PENNY_GREEN}
          tint={cashflow.goesNegativeOn ? 'red' : undefined}
          subtitle={cashflow.goesNegativeOn
            ? `⚠ projected OUT OF CASH on ${fmtLong(cashflow.goesNegativeOn)} — lowest ${gbp0(cashflow.minBalance)} on ${fmtLong(cashflow.minDate)}${cashflow.bufferCash > 0 ? ` (before the ${gbp0(cashflow.bufferCash)} savings buffer)` : ''}`
            : `lowest point ${gbp0(cashflow.minBalance)} on ${fmtLong(cashflow.minDate)} · ends at ${gbp0(cashflow.endBalance)}`}
        >
          <CashflowChart cashflow={cashflow} />
          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6 }}>
            Green dots are expected money landing. Living costs are spread daily; bills and debt payments hit on
            their recorded days — tell Penny each payment date she hasn't got, and the line gets truer.
          </div>
        </GlassCard>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)', gap: 18, alignItems: 'start' }}>
        {/* Debt stack */}
        <GlassCard title="Debt stack" subtitle={debts.length ? 'in payoff order' : null} icon={TrendingDown}
          iconBg={T.redLight} iconColor={T.red}>
          {debts.length ? debts.map(a => (
            <DebtRow key={a.id} account={a}
              maxBalance={Math.max(...debts.map(d => Number(d.balance)))}
              payoffDate={payoff?.debts?.find(d => d.id === a.id)?.payoffDate}
            />
          )) : (
            <div style={{ fontSize: 12.5, color: T.textMuted }}>No active debts recorded. Either you're debt-free — or the intake isn't finished.</div>
          )}
        </GlassCard>

        {/* Plan card */}
        <GlassCard title="The plan" icon={Target} iconBg="rgba(47,143,95,0.12)" iconColor={PENNY_GREEN}>
          {plan ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Chip color={PENNY_GREEN} bg="rgba(47,143,95,0.10)">{plan.strategy}</Chip>
                {plan.monthly_debt_budget != null && (
                  <Chip color={T.textMid} bg="rgba(0,0,0,0.05)">{gbp0(plan.monthly_debt_budget)}/mo to debt</Chip>
                )}
              </div>
              {payoff?.feasible && payoff.debtFreeDate && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Debt-free
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: PENNY_GREEN, letterSpacing: '-0.02em' }}>
                    {fmtLong(payoff.debtFreeDate)}
                  </div>
                  <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
                    {payoff.totalMonths} months · {gbp0(payoff.totalInterest)} total interest
                    {interestSaved != null && interestSaved > 0 && ` · saves ${gbp0(interestSaved)} vs minimums`}
                  </div>
                </div>
              )}
              {plan.emergency_fund_target != null && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: T.textMid }}>
                  <ShieldCheck size={14} color={PENNY_GREEN} />
                  Emergency fund: {gbp0(totals.savings)} of {gbp0(plan.emergency_fund_target)}
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: T.textMuted, lineHeight: 1.6 }}>
              No plan yet. Once your numbers are in, ask Penny to propose one — strategy,
              monthly debt budget, and the debt-free date that falls out of it.
            </div>
          )}
        </GlassCard>
      </div>

      {/* Payoff timeline */}
      {payoff?.feasible && payoff.months.length > 0 && (
        <GlassCard title="Payoff timeline" icon={CalendarClock}
          subtitle={baseline ? 'green = your plan · dotted = minimums only' : null}
          iconBg="rgba(47,143,95,0.12)" iconColor={PENNY_GREEN}>
          <PayoffTimeline payoff={payoff} baseline={baseline} accounts={overview.accounts} />
        </GlassCard>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 18, alignItems: 'start' }}>
        {/* Budget */}
        <GlassCard title="Monthly budget" icon={Wallet} iconBg={T.blueLight} iconColor={T.blue}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <BudgetBar budget={budget} />
            <div style={{ display: 'flex', gap: 14, fontSize: 11.5, color: T.textMuted, flexWrap: 'wrap' }}>
              <span><span style={{ color: T.violet, fontWeight: 700 }}>■</span> fixed {gbp0(budget.fixed)}</span>
              <span><span style={{ color: T.blue, fontWeight: 700 }}>■</span> variable {gbp0(budget.variable)}</span>
              <span><span style={{ color: PENNY_GREEN, fontWeight: 700 }}>■</span> surplus {gbp0(budget.surplus)}</span>
            </div>
            <div style={{ borderTop: '1px solid rgba(0,0,0,0.05)', paddingTop: 8 }}>
              {budget.items.map(i => (
                <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '3px 0' }}>
                  <span style={{ color: T.textMid }}>
                    {i.label} <span style={{ color: T.textLight, fontSize: 10.5 }}>({i.kind})</span>
                  </span>
                  <span style={{ color: i.kind === 'income' ? PENNY_GREEN : T.textMid, fontWeight: 600 }}>
                    {i.kind === 'income' ? '+' : '−'}{gbp0(i.amount_monthly)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </GlassCard>

        {/* Pension + progress */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <GlassCard title="Pension" icon={PiggyBank} iconBg="rgba(47,143,95,0.12)" iconColor={PENNY_GREEN}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: T.text }}>{gbp0(totals.pension)}</div>
                <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 2 }}>
                  {pension_projection
                    ? `projected ${gbp0(pension_projection.potAtTarget)} at ${pension_projection.series[pension_projection.series.length - 1].age}`
                    : plan?.pension_monthly_target
                      ? `restarting at ${gbp0(plan.pension_monthly_target)}/mo once debts clear`
                      : 'contributions paused — Rebuild phase'}
                </div>
              </div>
              {pension_projection && (
                <Sparkline points={pension_projection.series.map(p => p.pot)} color={PENNY_GREEN} />
              )}
            </div>
          </GlassCard>

          {snapshots.length > 1 && (
            <GlassCard title="Progress" icon={Banknote} iconBg={T.amberLight} iconColor={T.amber}
              subtitle={`since ${fmtLong(first.recorded_on)}`}>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: debtDelta <= 0 ? PENNY_GREEN : T.red }}>
                  {debtDelta <= 0 ? '−' : '+'}{gbp0(Math.abs(debtDelta))} debt
                </div>
                <Sparkline points={snapshots.map(s => s.total_debt)} color={debtDelta <= 0 ? PENNY_GREEN : T.red} />
              </div>
            </GlassCard>
          )}
        </div>
      </div>

      {/* Spending — statement uploads feed Penny's "where can I cut" advice */}
      <GlassCard title="Spending" icon={Receipt} iconBg={T.blueLight} iconColor={T.blue}
        subtitle={spending ? `from uploaded statements · ${spending.transactionCount} transactions` : 'upload a bank statement CSV and Penny can see where the money goes'}
        right={
          <div style={{ display: 'flex', gap: 8 }}>
            <motion.button
              onClick={() => setPasteOpen(o => !o)}
              disabled={importing}
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} transition={springs.snappy}
              style={{
                padding: '7px 14px', borderRadius: 10, cursor: importing ? 'default' : 'pointer',
                border: `1px solid ${PENNY_GREEN}55`, background: pasteOpen ? 'rgba(47,143,95,0.10)' : 'transparent',
                color: PENNY_GREEN, fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              Paste CSV
            </motion.button>
            <motion.button
              onClick={() => fileRef.current?.click()}
              disabled={importing}
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} transition={springs.snappy}
              style={{
                padding: '7px 14px', borderRadius: 10, border: 'none',
                cursor: importing ? 'default' : 'pointer',
                background: importing ? 'rgba(0,0,0,0.06)' : `linear-gradient(135deg, ${PENNY_GREEN}E6, ${PENNY_GREEN})`,
                color: importing ? T.textMuted : '#fff', fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              {importing ? <Loader2 size={13} className="spin" /> : <Upload size={13} />}
              {importing ? 'Importing…' : 'Upload statement'}
            </motion.button>
          </div>
        }>
        <input ref={fileRef} type="file" accept=".csv,text/csv,.tsv,text/tab-separated-values" onChange={onUpload} style={{ display: 'none' }} />
        {pasteOpen && (
          <div style={{ marginBottom: 14 }}>
            <textarea
              value={pasteText}
              onChange={e => setPasteText(e.target.value)}
              rows={6}
              placeholder={'Paste statement rows here — straight from your banking app, a CSV, or a spreadsheet (tabs are fine). A header row is optional.'}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '10px 12px',
                borderRadius: 10, border: '1px solid rgba(0,0,0,0.10)',
                background: 'rgba(255,255,255,0.85)', fontSize: 12, lineHeight: 1.5,
                fontFamily: 'ui-monospace, monospace', color: T.text, outline: 'none', resize: 'vertical',
              }}
            />
            <motion.button
              onClick={() => pasteText.trim() && importText(pasteText, 'Pasted CSV')}
              disabled={importing || !pasteText.trim()}
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} transition={springs.snappy}
              style={{
                marginTop: 8, padding: '8px 16px', borderRadius: 10, border: 'none',
                cursor: (importing || !pasteText.trim()) ? 'default' : 'pointer',
                background: (importing || !pasteText.trim()) ? 'rgba(0,0,0,0.06)' : `linear-gradient(135deg, ${PENNY_GREEN}E6, ${PENNY_GREEN})`,
                color: (importing || !pasteText.trim()) ? T.textMuted : '#fff',
                fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              {importing ? <Loader2 size={13} className="spin" /> : <Upload size={13} />}
              Import pasted rows
            </motion.button>
          </div>
        )}
        {importMsg && (
          <div style={{
            fontSize: 12, padding: '8px 12px', borderRadius: 9, marginBottom: 12,
            background: importMsg.startsWith('Import failed') ? T.redLight : 'rgba(47,143,95,0.10)',
            color: importMsg.startsWith('Import failed') ? T.red : PENNY_GREEN, fontWeight: 600,
          }}>{importMsg}</div>
        )}
        {spending ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 20, alignItems: 'start' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                {fmtMonth(spending.currentMonth + '-15')}: {gbp0(spending.totalSpendCurrentMonth)}
                <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}> vs {gbp0(spending.totalSpendPrevMonth)} last month</span>
              </div>
              {spending.topCategories.slice(0, 7).map(c => {
                const max = spending.topCategories[0]?.amount || 1;
                return (
                  <div key={c.category} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12 }}>
                    <span style={{ width: 96, color: T.textMid, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.category.replace(/_/g, ' ')}
                    </span>
                    <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                      <div style={{ width: `${(c.amount / max) * 100}%`, height: '100%', borderRadius: 3,
                        background: c.cuttable ? T.blue : T.textLight }} />
                    </div>
                    <span style={{ width: 56, textAlign: 'right', fontWeight: 650, color: T.text }}>{gbp0(c.amount)}</span>
                    <span style={{ width: 52, textAlign: 'right', fontSize: 10.5,
                      color: c.delta > 0 ? T.red : c.delta < 0 ? PENNY_GREEN : T.textLight }}>
                      {c.delta ? `${c.delta > 0 ? '+' : '−'}${gbp0(Math.abs(c.delta))}` : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Recurring payments
              </div>
              {spending.recurring.length ? spending.recurring.slice(0, 7).map(r => (
                <div key={r.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '3px 0', fontSize: 12 }}>
                  <span style={{ color: T.textMid, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                  <span style={{ fontWeight: 650, color: T.text, whiteSpace: 'nowrap' }}>
                    {gbp0(r.avgAmount)}/mo <span style={{ color: T.textLight, fontWeight: 500 }}>({gbp0(Math.round(r.avgAmount * 12))}/yr)</span>
                  </span>
                </div>
              )) : (
                <div style={{ fontSize: 12, color: T.textMuted }}>Needs 2+ months of statements to spot recurring payments.</div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: T.textMuted, lineHeight: 1.6 }}>
            No transactions yet. Export a CSV from your banking app and upload it — Penny categorises
            everything, spots the recurring payments, and can tell you exactly where the cuts are.
          </div>
        )}
      </GlassCard>
    </div>
  );
};
